// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import L from 'leaflet';
import Backbone from 'backbone';
import myw from 'myWorld-base';
import mapLinkHtml from 'text!html/mapLink.html';

myw.MapLinkPlugin = myw.Plugin.extend({
    options: {
        generateNativeAppLink: false, //Used by the 'Copy link' button
        displayCoreLink: true,
        displayNativeAppLink: false
    },

    /**
     * @class Functionality to generate/send links with the current state of the application<br/>
     * Adds a button to the toolbar to access a dialog which allows the user copy or send a link with the current state of the map/application
     * @param  {Application} owner    The application
     * @param  {mapLinkOptions}  options
     * @constructs
     * @extends {Plugin}
     */
    initialize(owner, options) {
        myw.Plugin.prototype.initialize.call(this, owner, options);
    },

    /**
     * Shows the map link window.
     */
    showDialog() {
        this.coreLink = this.app.getAppLink();
        this.nativeAppLink = myw.app.getAppLink(true);

        if (this.app.isHandheld) {
            const overlay = this.app.layout.pages.mapLink.show();
            overlay.on('click', '.email_link_btn', () => {
                this._navigateToEmailLink();
            });
            return;
        }

        if (!this.linkDialog) {
            //Add the html for the maplink dialogue
            this.linkDialog = new MapLinkDialog(this, {
                displayCoreLink: this.options.displayCoreLink,
                generateNativeAppLink: this.options.generateNativeAppLink,
                displayNativeAppLink: this.options.displayNativeAppLink
            });
        }
        this.linkDialog.open(this.coreLink, this.nativeAppLink);
    },

    /**
     * Creates an email message with the configured links
     * Closes the dialog
     * @private
     */
    _navigateToEmailLink() {
        const emailSubject = this.msg('email_subject');
        let emailBody;

        if (!this.options.generateNativeAppLink) {
            emailBody = encodeURIComponent(
                this.msg('email_body', { url: this.coreLink.replace(/ /g, '%20') })
            );
        } else if (!this.options.displayCoreLink) {
            emailBody = encodeURIComponent(
                this.msg('email_body', { url: this.nativeAppLink.replace(/ /g, '%20') })
            );
        } else {
            const coreLinkMsg = this.msg('core_link_msg') + this.coreLink.replace(/ /g, '%20');
            const nativeAppLinkMsg =
                this.msg('native_app_link_msg') + this.nativeAppLink.replace(/ /g, '%20');
            emailBody = `${this.msg('email_msg')}%0D%0A${encodeURIComponent(
                coreLinkMsg
            )}%0D%0A${encodeURIComponent(nativeAppLinkMsg)}`;
        }
        window.location.href = `mailto:someone@example.com?subject=${encodeURIComponent(
            emailSubject
        )}&body=${emailBody}`;
        this.close();
    },

    /**
     * Close the map link UI
     */
    close() {
        if (this.app.isHandheld) this.app.layout.pages.mapLink.close();
        else this.linkDialog.close();
    }
});

const MapLinkDialog = myw.Control.extend({
    id: 'link-panel',
    className: 'panel',
    /*
     * @class Creates a dialog with the link provided and adds it to the HTML body
     * @param  {object}            options
     * @param  {MapLinkPlugin} options.owner           The application
     * @constructs
     * @extends {Backbone.View}
     */
    initialize(owner, options) {
        myw.Control.prototype.initialize.call(this, owner, options);
        this.owner = owner;
        // this.options = options;
        this.render();
    },

    render() {
        //Add the html for the maplink dialogue
        this.$el.appendTo('body');

        if (this.options.displayCoreLink) {
            this.coreLinkBox = new MapLinkBox({ owner: this, app_type: 'core' });
            this.$el.append(this.coreLinkBox.$el);
        }
        if (this.options.displayNativeAppLink) {
            this.nativeAppLinkBox = new MapLinkBox({ owner: this, app_type: 'native' });
            this.$el.append(this.nativeAppLinkBox.$el);
        }

        if (!this.options.displayNativeAppLink || !this.options.displayCoreLink) {
            this.$('.map-link-header').hide();
        }

        this.$el
            .dialog({
                modal: true,
                autoOpen: false,
                minWidth: 480,
                position: { my: 'center', at: 'top+193', of: window },
                title: this.owner.msg('maplink_title'),
                closeText: this.owner.msg('close_tooltip'),
                buttons: {
                    Email: {
                        text: this.owner.msg('email_btn'),
                        class: 'email_link_btn primary-btn',
                        click: () => {
                            this.owner._navigateToEmailLink();
                        }
                    },
                    Copy: {
                        text: this.owner.msg('copy_link'),
                        class: 'copy_link_btn',
                        click: e => {
                            this.copyLinkToClipboard(e);
                        }
                    },
                    Close: {
                        text: this.owner.msg('close_btn'),
                        class: 'right',
                        click: function() {
                            $(this).dialog('close');
                        }
                    }
                }
            })
            .dialog('widget')
            .find('.ui-dialog-buttonset')
            .css('float', 'none');
    },

    /*
     * Opens the dialog and updates the textarea with the link supplied
     */
    open(coreLink, nativeAppLink) {
        this.$el.dialog('open');
        this.coreLink = coreLink;
        this.nativeAppLink = nativeAppLink;

        if (this.coreLinkBox)
            this.coreLinkBox
                .$('textarea')
                .val(coreLink)
                .blur();
        if (this.nativeAppLinkBox)
            this.nativeAppLinkBox
                .$('textarea')
                .val(nativeAppLink)
                .blur();
    },

    close() {
        this.$el.dialog('close');
    },

    /*
     * Adds the links(based on the configured options) to a temporary div and selects them (so they can be copied to clipboard)
     */
    _selectLink() {
        if (L.Browser.ie) {
            //For IE11
            const textRange = document.body.createTextRange();
            textRange.moveToElementText(this.tempDiv[0]);
            textRange.select();
            textRange.execCommand('Copy');
        }

        if (window.getSelection) {
            const selection = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(this.tempDiv[0]);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    },

    /*
     * Copies the link in the textarea to the clipboard
     */
    copyLinkToClipboard(e) {
        e.preventDefault();
        this.tempDiv = $('<div>', { class: 'all-links' }).appendTo(this.$el);
        this.tempDiv.html(`<a href=${this.coreLink}>${this.coreLink}</a>`);

        if (this.options.generateNativeAppLink) {
            this.tempDiv.prepend(this.owner.msg('core_link_msg'));
            this.tempDiv.append(
                `</br>${this.owner.msg('native_app_link_msg')}<a href=${this.nativeAppLink}>${
                    this.nativeAppLink
                }</a>`
            );
        }
        // Select the links
        this._selectLink();
        document.execCommand('copy');
        this.tempDiv.remove();
    }
});

const MapLinkBox = Backbone.View.extend({
    template: _.template(
        $(mapLinkHtml)
            .filter('#map-link-box-template')
            .html()
    ),
    events: {
        'click textarea': 'selectLink'
    },

    /*
     * @class Creates a box with a textarea to house the map link
     * @param  {object}        options
     * @param  {MapLinkDialog} options.owner
     * @param  {string}        options.app_link   Either 'core' or 'native'
     * @constructs
     * @extends {Backbone.View}
     */
    initialize(options) {
        this.options = options;
        this.owner = this.options.owner;
        this.render();
    },

    render() {
        this.$el.html(
            this.template({
                app_type: this.owner.owner.msg(this.options.app_type),
                copy_link: this.owner.owner.msg('copy_link')
            })
        );
    },

    /*
     * Event handler for when the users clicks the texarea that has the link
     */
    selectLink() {
        this.$('textarea').select();
    }
});

myw.MapLinkPlugin.prototype.buttons = {
    dialog: myw.PluginButton.extend({
        id: 'a-mapLink',
        titleMsg: 'toolbar_msg',
        imgSrc: 'images/toolbar/link.svg',
        action() {
            this.app.recordFunctionalityAccess('core.toolbar.maplink');
            this.owner.showDialog();
        }
    })
};

/**
 * Options to configure the map link functionality
 * @typedef mapLinkOptions
 * @property {boolean}    generateNativeAppLink   Whether to add a link to myWorld native app to the 'email'/'copy to clipboard' option
 * @property {boolean}    displayCoreLink         Whether to show a textbox with the link to the myWorld core application
 * @property {boolean}    displayNativeAppLink    Whether to show a textbox with the link to the myWorld native app
 */

export default myw.MapLinkPlugin;
